import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Collapse,
  Link
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import CodeIcon from '@mui/icons-material/Code';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from '../../services/apiClient';

const Indexierung = () => {
  const [indexStatus, setIndexStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [progress, setProgress] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showSqlQuery, setShowSqlQuery] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/index/status');
      setIndexStatus(response.data);
      
      // Setze Reindexing-Status basierend auf Server-Antwort
      const isIndexing = response.data.status === 'indexing';
      setReindexing(isIndexing);
      
      // Update Progress wenn verfügbar
      if (response.data.indexing_progress) {
        setProgress(response.data.indexing_progress);
      } else {
        setProgress(null);
      }
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Laden des Index-Status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Funktion für das Status-Polling
  const startStatusPolling = useCallback(() => {
    const pollStatus = async () => {
      try {
        const response = await apiClient.get('/index/status');
        
        // Update Progress
        if (response.data.indexing_progress) {
          setProgress(response.data.indexing_progress);
        }
        
        // Setze Reindexing-Status basierend auf Server-Antwort
        const isIndexing = response.data.status === 'indexing';
        setReindexing(isIndexing);
        
        // Wenn noch indexiert wird, weiter pollen
        if (isIndexing) {
          setTimeout(pollStatus, 2000);
        } else {
          // Fertig oder Fehler
          setReindexing(false);
          setProgress(null);
          await fetchStatus();
        }
      } catch (err) {
        console.error('Fehler beim Status-Polling:', err);
        setError('Fehler beim Prüfen des Indexierungsstatus');
        setReindexing(false);
        setProgress(null);
      }
    };
    
    // Starte das erste Polling sofort
    pollStatus();
  }, [fetchStatus]);

  const handleReindex = async () => {
    try {
      setReindexing(true);
      setError(null);
      setProgress(null);
      
      // Starte die Neuindexierung
      await apiClient.post('/index/reindex');
      
      // Starte das Status-Polling
      startStatusPolling();
      
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Eine Indexierung läuft bereits');
      } else {
        setError(err.response?.data?.detail || 'Fehler bei der Neuindexierung');
      }
      setReindexing(false);
      setProgress(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    handleSearch(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    handleSearch(1, newRowsPerPage);
  };

  const handleSearch = async (currentPage = page + 1, currentRowsPerPage = rowsPerPage) => {
    if (!searchQuery.trim()) return;

    try {
      setSearching(true);
      setError(null);
      const response = await apiClient.get('/index/search_debug', {
        params: { 
          query: searchQuery,
          page: Number(currentPage),
          page_size: Number(currentRowsPerPage)
        }
      });
      setSearchResults(response.data);
    } catch (err) {
      // Verbesserte Fehlerbehandlung
      let errorMessage = 'Fehler bei der Suche';
      if (err.response?.data?.detail) {
        // Wenn der Fehler ein Array ist (FastAPI Validierungsfehler)
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail
            .map(error => error.msg)
            .join(', ');
        }
        // Wenn der Fehler ein Objekt ist
        else if (typeof err.response.data.detail === 'object') {
          errorMessage = err.response.data.detail.msg || JSON.stringify(err.response.data.detail);
        }
        // Wenn der Fehler ein String ist
        else {
          errorMessage = err.response.data.detail;
        }
      }
      setError(errorMessage);
      setSearchResults(null);
    } finally {
      setSearching(false);
    }
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setSelectedResult(null);
  };

  const handleDetailsOpen = (result) => {
    setSelectedResult(result);
    setDetailsOpen(true);
  };

  useEffect(() => {
    const initializeStatus = async () => {
      await fetchStatus();
      
      // Nach dem ersten fetchStatus, prüfe ob eine Indexierung läuft und starte ggf. Polling
      const response = await apiClient.get('/index/status');
      const isIndexing = response.data.status === 'indexing';
      if (isIndexing) {
        startStatusPolling();
      }
    };
    
    initializeStatus();
  }, [fetchStatus, startStatusPolling]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        FTS-Index Verwaltung
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Index-Status
          </Typography>
          <Tooltip title="Status aktualisieren">
            <IconButton onClick={fetchStatus} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {indexStatus && (
          <>
            <Typography color="text.secondary" gutterBottom>
              Status: {indexStatus.status === 'ok' ? 'OK' : indexStatus.status === 'incomplete' ? 'Unvollständig' : 'Nicht gefunden'}
            </Typography>
            {indexStatus.stats && (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Gesamtanzahl Dokumente</TableCell>
                      <TableCell align="right">{indexStatus.stats.total_documents}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Indexierte Dokumente</TableCell>
                      <TableCell align="right">{indexStatus.stats.indexed_documents}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Dokumente mit Text</TableCell>
                      <TableCell align="right">{indexStatus.stats.documents_with_text}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleReindex}
            disabled={reindexing}
            startIcon={reindexing ? <CircularProgress size={20} /> : null}
          >
            {reindexing ? 'Indexiere...' : 'Neu indexieren'}
          </Button>
          
          {progress && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {progress.phase_message}
              </Typography>
              
              {/* Phase Progress */}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Fortschritt aktuelle Phase: {progress.phase_progress}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={progress.phase_progress} 
                sx={{ mt: 1, mb: 2 }}
              />
              
              {/* Gesamtfortschritt nur während der Indexierungsphase */}
              {progress.phase === 'indexing' && (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Gesamtfortschritt: {progress.processed} von {progress.total} Dokumenten ({progress.percentage}%)
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={progress.percentage} 
                    sx={{ mt: 1 }}
                  />
                </>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Index-Debug
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            label="Suchbegriff"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch(1, rowsPerPage)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={() => handleSearch(1, rowsPerPage)}
            disabled={searching || !searchQuery.trim()}
            startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
          >
            Suchen
          </Button>
        </Box>

        {searchResults && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Suchergebnisse
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Verwendete FTS-Query: <code>{searchResults.fts_query}</code>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {searchResults.stats.matching_documents} Treffer gefunden
              (zeige {searchResults.stats.shown_results} von {searchResults.stats.matching_documents})
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Button
                onClick={() => setShowSqlQuery(!showSqlQuery)}
                startIcon={<CodeIcon />}
                endIcon={showSqlQuery ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                size="small"
                sx={{ mb: 1 }}
              >
                SQL-Query {showSqlQuery ? 'ausblenden' : 'anzeigen'}
              </Button>
              
              <Collapse in={showSqlQuery}>
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, overflow: 'auto' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                    <code>{searchResults.actual_sql_query}</code>
                  </pre>
                </Box>
              </Collapse>
            </Box>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Aktenzeichen</TableCell>
                    <TableCell>Relevanz</TableCell>
                    <TableCell>Fundstelle im Text</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.results
                    .sort((a, b) => b.relevance - a.relevance)
                    .map((result) => (
                    <TableRow key={result.id}>
                      <TableCell>{result.id}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Link
                            component={RouterLink}
                            to={`/entscheidungen/${result.id}`}
                            sx={{ textDecoration: 'none' }}
                          >
                            {result.aktenzeichen}
                          </Link>
                          <Tooltip title="In neuem Tab öffnen">
                            <IconButton
                              size="small"
                              onClick={() => window.open(`/entscheidungen/${result.id}`, '_blank')}
                              sx={{ 
                                padding: '2px',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                              }}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: result.relevance > 90 ? 'bold' : 'normal',
                          color: result.relevance > 90 ? 'success.main' : 
                                 result.relevance > 70 ? 'primary.main' : 
                                 'text.primary'
                        }}
                      >
                        {result.relevance}%
                      </TableCell>
                      <TableCell 
                        dangerouslySetInnerHTML={{ 
                          __html: result.snippet?.replace(/\[/g, '<mark>').replace(/\]/g, '</mark>') || 'Kein Textauszug verfügbar'
                        }} 
                        sx={{ 
                          maxWidth: '500px',
                          minWidth: '300px',
                          fontSize: '0.875rem',
                          lineHeight: '1.4',
                          '& mark': { 
                            backgroundColor: 'yellow',
                            padding: '0 2px',
                            borderRadius: '2px'
                          }
                        }}
                      />
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleDetailsOpen(result)}
                          title="Details anzeigen"
                        >
                          <InfoIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {searchResults.results.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Keine Treffer gefunden
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {searchResults.stats.matching_documents} Treffer gefunden
              </Typography>
              <TablePagination
                component="div"
                count={searchResults.pagination.total_results}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} von ${count}`
                }
                labelRowsPerPage="Zeilen pro Seite:"
              />
            </Box>
          </Box>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleDetailsClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Suchergebnis Details
        </DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Dokument-Information
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell component="th">ID</TableCell>
                      <TableCell>{selectedResult.id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">Aktenzeichen</TableCell>
                      <TableCell>{selectedResult.aktenzeichen}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">Relevanz</TableCell>
                      <TableCell>{selectedResult.relevance}%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Statistiken
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell component="th">Ranking Score (BM25)</TableCell>
                      <TableCell>{selectedResult.details.rank_score.toFixed(4)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">Gesamtanzahl Dokumente</TableCell>
                      <TableCell>{selectedResult.details.total_documents}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">Gefundene Dokumente</TableCell>
                      <TableCell>{selectedResult.details.matching_documents}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th">Trefferquote</TableCell>
                      <TableCell>
                        {(selectedResult.details.matching_documents / selectedResult.details.total_documents * 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Textausschnitt
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                <Typography 
                  variant="body2" 
                  component="div"
                  dangerouslySetInnerHTML={{ 
                    __html: selectedResult.snippet || 'Kein Textausschnitt verfügbar'
                  }}
                  sx={{
                    '& mark': {
                      backgroundColor: 'yellow',
                      padding: '0 2px',
                      borderRadius: '2px'
                    }
                  }}
                />
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailsClose}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Indexierung; 